import React from 'react';

import { formatMoney, toMoney } from '@betterment/core';
import { Icon, Text, Tooltip } from '@betterment/samba-react';
import { token } from '@betterment/samba-styled-system/tokens';
import { Box, Flex, HR, StackedProgressBar } from '@betterment/style-closet-react';

import { EmployerFourOhOneKContributionConfig, EmployerFourOhOneKContributionCurrentYear } from '../../data';
import ReffableIcon from '../ReffableIcon';

const CURRENT_CONTRIBUTIONS_COLOR = 'dataviz.teal.1';
const PROJECTED_CONTRIBUTIONS_COLOR = 'dataviz.teal.3';

const totalProjectedAmount = (employerFourOhOneKContribution: EmployerFourOhOneKContributionCurrentYear): number => {
  return employerFourOhOneKContribution.projection?.total?.cents || 0;
};

const eligibleAmount = (
  employerFourOhOneKContributionConfig: EmployerFourOhOneKContributionConfig,
  employerFourOhOneKContributionsCurrentYear: EmployerFourOhOneKContributionCurrentYear
): number => {
  return (
    employerFourOhOneKContributionConfig?.limit?.cents || employerFourOhOneKContributionsCurrentYear.amount.cents || 0
  );
};

const projectedFutureContributionAmount = (
  employerFourOhOneKContribution: EmployerFourOhOneKContributionCurrentYear
): number => {
  return totalProjectedAmount(employerFourOhOneKContribution) - employerFourOhOneKContribution.amount.cents;
};

const progressBarData = (
  employerFourOhOneKContributionConfig: EmployerFourOhOneKContributionConfig,
  employerFourOhOneKContributionsCurrentYear: EmployerFourOhOneKContributionCurrentYear
) => {
  const currentContributionPercent =
    (employerFourOhOneKContributionsCurrentYear.amount.cents /
      eligibleAmount(employerFourOhOneKContributionConfig, employerFourOhOneKContributionsCurrentYear)) *
    100;
  const projectedContributionPercent =
    (projectedFutureContributionAmount(employerFourOhOneKContributionsCurrentYear) /
      eligibleAmount(employerFourOhOneKContributionConfig, employerFourOhOneKContributionsCurrentYear)) *
    100;
  return [
    {
      percent: currentContributionPercent,
      indicatorColor: token.var(`colors.${CURRENT_CONTRIBUTIONS_COLOR}`),
    },
    {
      percent: projectedContributionPercent,
      indicatorColor: token.var(`colors.${PROJECTED_CONTRIBUTIONS_COLOR}`),
    },
  ];
};

interface Props {
  employerFourOhOneKContributionConfig: EmployerFourOhOneKContributionConfig;
  employerFourOhOneKContributionsCurrentYear: EmployerFourOhOneKContributionCurrentYear;
}

const CurrentYearContributions: React.FC<React.PropsWithChildren<Props>> = ({
  employerFourOhOneKContributionConfig,
  employerFourOhOneKContributionsCurrentYear,
}) => {
  return (
    <>
      <Box mb="3" mt="6">
        <StackedProgressBar
          percents={progressBarData(employerFourOhOneKContributionConfig, employerFourOhOneKContributionsCurrentYear)}
        />
        <Flex justifyContent="space-between" mt="1">
          <Text
            color="text.weak"
            css={{
              fontSize: 'fontSize.50',
            }}
          >
            $0
          </Text>
          <Flex>
            <Text
              color="text.weak"
              css={{
                fontSize: 'fontSize.50',
              }}
            >
              Eligible:{' '}
              {formatMoney(
                toMoney(
                  eligibleAmount(employerFourOhOneKContributionConfig, employerFourOhOneKContributionsCurrentYear)
                )
              )}
            </Text>
          </Flex>
        </Flex>
      </Box>
      <Flex justifyContent="space-between" my="6">
        <Flex>
          <Icon name="Bullet" size="small" color={CURRENT_CONTRIBUTIONS_COLOR} />
          <Text>Match Earned</Text>
          <Tooltip>
            <Tooltip.Trigger asChild={true}>
              <ReffableIcon name="InfoCircle" size="small" css={{ verticalAlign: 'middle' }} />
            </Tooltip.Trigger>
            <Tooltip.Content side="bottom">Match earned does not include any market changes.</Tooltip.Content>
          </Tooltip>
        </Flex>
        <Text>{formatMoney(employerFourOhOneKContributionsCurrentYear.amount)}</Text>
      </Flex>
      <HR />
      {employerFourOhOneKContributionConfig && (
        <>
          <Flex justifyContent="space-between" my="6">
            <Flex>
              <Icon name="Bullet" size="small" color={PROJECTED_CONTRIBUTIONS_COLOR} />
              <Text>
                Projected match
                <br />
                <Text
                  color="text.weak"
                  css={{
                    fontSize: 'fontSize.50',
                  }}
                >
                  Based on your contributions
                </Text>
              </Text>
            </Flex>
            <Text>
              {formatMoney(toMoney(projectedFutureContributionAmount(employerFourOhOneKContributionsCurrentYear)))}
            </Text>
          </Flex>
          <HR />
        </>
      )}
      <Flex justifyContent="space-between" my="6">
        <Text>Total match</Text>
        <Text>{formatMoney(toMoney(totalProjectedAmount(employerFourOhOneKContributionsCurrentYear)))}</Text>
      </Flex>
    </>
  );
};

export default CurrentYearContributions;
